import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import * as signalR from '@microsoft/signalr';
import {AuthService} from './auth.service';
import {IAuthData} from '../models/IAuthData';
import {Store} from '@ngrx/store';
import {IAppState} from '../models/IAppState';
import {Observable, Subject, Subscription} from 'rxjs';
import {setCheckIn} from '../store/actions/location.actions';
import {ICheckin} from '../models/ICheckin';
import {IReservation} from '../models/IReservation';
import {DesktopNotificationService} from './desktop-notification.service';
import {TranslationService} from '../translation';
import {NavController} from '@ionic/angular';
import moment from 'moment';
import {ChatVisitorUpdateEvent, ExtractChatEvent} from '../models/IChatBot';
import {setLanguage} from '../store/actions/user.actions';


export interface ICheckinEvent {
  checkinId: string;
  checkinStatus: number;
  locationId: string;
}

export interface IReservationEvent {
  reservationId: string;
  reservationStatus: number;
  // locationId: string;
}

@Injectable({
  providedIn: 'root'
})
export class RtcService {
  public authData$: Observable<IAuthData> = this.store.select(
    (state) => state.AppStateReducer.authData
  );
  public chatExport$: Subject<ExtractChatEvent> = new Subject();
  public visitorUpdate$: Subject<ChatVisitorUpdateEvent> = new Subject();
  public reservations$: Subject<any> = new Subject();
  public checkins$: Subject<any> = new Subject();
  private nextReloadTime = moment().add(6, 'hours');
  private hasInit = false;
  private connection: signalR.HubConnection;
  private state$: Observable<IAppState> = this.store.select((state) => state.AppStateReducer);
  private selectedCheckInId: string;
  private stateSub: Subscription;

  constructor(
    private authService: AuthService,
    private ts: TranslationService,
    private navCtrl: NavController,
    private desktopNotificationService: DesktopNotificationService,
    private store: Store<{AppStateReducer: IAppState}>
  ) {
    console.log('RtcService', this);
    this.stateSub = this.state$.subscribe(
      (appState: IAppState) => {
        if (appState.selectedCheckIn && appState.selectedCheckIn.id) {
          this.selectedCheckInId = appState.selectedCheckIn.id;
        }

        if (!this.hasInit && (appState.authData || appState.userData)) {
          this.init();
        }
      });
  }

  init() {
    this.setAutoReload();
    this.hasInit = true;
    console.log('RtcService init');
    if (this.connection) {
      this.connection.stop();
    }
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalr, {
        accessTokenFactory: () => this.authService.getToken()
      })
      .withAutomaticReconnect()
      .build();
    this.connection.keepAliveIntervalInMilliseconds = 1000 * 60 * 2;
    this.connection.serverTimeoutInMilliseconds = 1000 * 60 * 6;
    this.connection.start();

    this.connection.on('checkinStatus', (data: any) => {
      // filter out old format
      console.log('SignalR checkinStatus: ', data);
      const checkin: ICheckin = data.checkIn;
      if (!checkin.id) {
        return;
      }
      if (this.selectedCheckInId === checkin.id) {
        this.store.dispatch(setCheckIn({id: checkin.id, checkin}));
      }
      if (data.triggeredEvent?.toLowerCase().indexOf('queue') === -1) {
        this.desktopNotificationService.showDesktopNotification({
          eventHash: data.triggeredEvent + '_' + checkin.id,
          event: data.triggeredEvent,
          locationId: checkin.location.id,
          text: checkin.location.name,
          body: this.ts.instant(data.triggeredEvent + ' ' + checkin.status),
          icon: 'assets/imgs/logo.png',
          onClick: () => this.navCtrl.navigateForward(['/check-in-registration/' + checkin.id])
        });
      }
      this.checkins$.next(checkin.id);
    });

    this.connection.on('eventStatus', (message) => {
      console.log('SignalR 2: ', message);
    });

    this.connection.on('reservationStatus', (data: any) => {
      console.log('SignalR reservationStatus: ', data);
      const reservation: IReservation = data.reservation;

      // filter out old format
      if (!reservation.id) {
        return;
      }
      this.desktopNotificationService.showDesktopNotification({
        eventHash: data.triggeredEvent + '_' + reservation.id,
        text: reservation.location.name,
        event: data.triggeredEvent,
        locationId: reservation.location.id,
        body: this.ts.instant(data.triggeredEvent + ' ' + reservation.status),
        icon: 'assets/imgs/logo.png',
        onClick: () => this.navCtrl.navigateForward(['/reservation/' + reservation.id])
      });
      this.reservations$.next(reservation);
    });

    this.connection.on('chatHistoryExtractionFinished', (data: ExtractChatEvent) => {
      this.chatExport$.next(data);
    });

    this.connection.on('visitorUpdated', (event: ChatVisitorUpdateEvent) => {
      if (event?.language) {
        this.store.dispatch(setLanguage({language: event.language}));
      }

      this.visitorUpdate$.next(event);
    });
  }

  setAutoReload() {
    setInterval(() => {
      if (moment().isAfter(this.nextReloadTime) && !document.hasFocus()) {
        window.location.reload();
      }
    }, 10000);
  }
}
